import { useState, useEffect, useCallback, useRef } from 'react';
import { useConsultaOperadora } from './useConsultaOperadora';
import { phoneOperatorCache } from '@/lib/phone-operator-cache';
import { onlyDigits, normalizePhoneRAW, adicionarDigito9 } from '@/utils/phone';
import { supabase } from '@/integrations/supabase/client';
import { normalizarOperadora } from '@/utils/operadoraNormalizer';

// (Douglas 27/07) Limpeza única das marcas `phone-operator-consulted:` que o
// bloqueio antigo deixou espalhadas. O código não as lê nem grava mais, mas ficam
// sujando o localStorage de quem usou o CRM hoje.
if (!localStorage.getItem('phone-operator-bloqueio-removido')) {
  Object.keys(localStorage)
    .filter((k) => k.startsWith('phone-operator-consulted:'))
    .forEach((k) => localStorage.removeItem(k));
  localStorage.setItem('phone-operator-bloqueio-removido', '1');
}

// ✅ Limpar cache inválido do localStorage (executar uma vez)
if (!localStorage.getItem('cache-cleanup-v1-done')) {
  console.log('[usePhoneOperator] 🧹 Limpando cache inválido...');
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith('phone-operator-cache:')) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const entry = JSON.parse(raw);
          if (entry.operadora === 'DESCONHECIDO' || entry.operadora === 'ERRO' || entry.operadora === 'OUTRA') {
            console.log('[usePhoneOperator] 🗑️ Removendo cache inválido:', key);
            localStorage.removeItem(key);
          }
        }
      } catch (e) {}
    }
  });
  localStorage.setItem('cache-cleanup-v1-done', 'true');
  console.log('[usePhoneOperator] ✅ Limpeza de cache concluída');
}

interface UsePhoneOperatorReturn {
  operadora: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Detecta tipo de telefone
 * - CELULAR: 11 dígitos (2 DDD + 9 + 8 linha) ou 10 dígitos com primeiro dígito 9/8/7
 * - FIXO: 10 dígitos (2 DDD + 8 linha) com primeiro dígito 2/3/4/5
 */
function detectType(phone: string): 'CELULAR' | 'FIXO' | 'INVALIDO' {
  const digits = onlyDigits(phone);
  
  let localNumber = digits;
  if (digits.startsWith('55') && digits.length >= 12) {
    localNumber = digits.substring(2);
  }
  
  // Aceitar 10 ou 11 dígitos
  if (localNumber.length < 10 || localNumber.length > 11) {
    return 'INVALIDO';
  }
  
  const ddd = parseInt(localNumber.substring(0, 2));
  if (ddd < 11 || ddd > 99) {
    return 'INVALIDO';
  }
  
  const firstDigit = localNumber.charAt(2);
  
  // 11 dígitos com 9 na frente = CELULAR
  if (localNumber.length === 11 && firstDigit === '9') {
    return 'CELULAR';
  }
  
  // 10 dígitos com 9/8/7 = CELULAR (formato antigo)
  if (localNumber.length === 10 && ['9', '8', '7'].includes(firstDigit)) {
    return 'CELULAR';
  }
  
  // 10 dígitos com 2/3/4/5 = FIXO
  if (localNumber.length === 10 && ['2', '3', '4', '5'].includes(firstDigit)) {
    return 'FIXO';
  }
  
  // 11 dígitos mas não começa com 9 = provavelmente inválido
  if (localNumber.length === 11 && !['9'].includes(firstDigit)) {
    return 'INVALIDO';
  }
  
  return 'INVALIDO';
}

/**
 * Hook para consultar operadora de um número com cache automático
 * - Valida se é celular
 * - Cache de 24h em localStorage
 * - Debounce de 500ms
 * - Retry logic (2 tentativas)
 */
export function usePhoneOperator(phoneNumber: string | undefined | null): UsePhoneOperatorReturn {
  const [operadora, setOperadora] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  console.log('[usePhoneOperator] Hook chamado com phoneNumber:', phoneNumber);
  
  const { consultarRapida } = useConsultaOperadora();

  const validateAndNormalize = useCallback((phone: string | undefined | null): string | null => {
    if (!phone) {
      console.log('[usePhoneOperator] ⚠️ Telefone vazio ou nulo');
      return null;
    }
    
    const digits = onlyDigits(phone);
    console.log('[usePhoneOperator] 🔢 Dígitos extraídos:', digits, '(length:', digits.length + ')');
    
    // Detectar tipo de telefone
    const tipo = detectType(phone);
    console.log('[usePhoneOperator] 📱 Tipo detectado:', tipo);
    
    // ✅ CORREÇÃO: Telefone FIXO - normalizar e consultar API para obter operadora real
    // A API consulta-operadora suporta telefones fixos (10 dígitos) e retorna operadora real (VIVO, NET, OI, etc)
    if (tipo === 'FIXO') {
      // Para FIXO, normalizar sem adicionar o dígito 9
      const normalized = normalizePhoneRAW(phone);
      console.log('[usePhoneOperator] 📞 Telefone FIXO detectado - consultando API:', normalized);
      return normalized; // Retorna 10-12 dígitos (55 + DDD + 8 dígitos)
    }
    
    // Telefone INVÁLIDO
    if (tipo === 'INVALIDO') {
      console.log('[usePhoneOperator] ❌ Número inválido');
      setError('Número inválido');
      setIsLoading(false);
      return null;
    }
    
    // CELULAR válido - normalizar para formato RAW e adicionar 9 para consulta
    const normalized = normalizePhoneRAW(phone);
    const numeroComNove = adicionarDigito9(normalized);
    console.log('[usePhoneOperator] ✅ Celular normalizado (RAW):', normalized);
    console.log('[usePhoneOperator] ✅ Celular para consulta (COM 9):', numeroComNove);
    return numeroComNove;
  }, []);

  const fetchOperadora = useCallback(async (normalizedPhone: string, retryCount = 0) => {
    try {
      console.log('[usePhoneOperator] 🔍 Iniciando consulta:', normalizedPhone);
      setIsLoading(true);
      setError(null);

      // 1. Tentar cache local primeiro (instantâneo)
      const cached = phoneOperatorCache.get(normalizedPhone);

      console.log('[usePhoneOperator] 💾 Cache local:', cached || 'não encontrado');
      
      // (27/07) 'OUTRA' SAIU DA LISTA DE INVÁLIDOS. Ela não é falha de consulta:
      // é operadora que a API achou e o nosso normalizador não conhece (ele só
      // mapeia CLARO/TIM/OI/VIVO/NEXTEL). Tratada como inválida, o cache era
      // limpo a cada passada, a consulta refeita, e o campo girava pra sempre
      // sem nunca entregar a doadora — gastando a API externa em cada volta.
      if (cached) {
        if (cached !== 'DESCONHECIDO' && cached !== 'ERRO') {
          console.log('[usePhoneOperator] ✅ Cache localStorage válido:', cached);
          setOperadora(cached);
          setIsLoading(false);
          return;
        } else {
          console.log('[usePhoneOperator] ⚠️ Cache inválido, limpando:', cached);
          phoneOperatorCache.clear(normalizedPhone);
        }
      }
      
      // 3. Consultar tabela telefones_operadora (cache 60 dias - operadora raramente muda)
      console.log('[usePhoneOperator] 🗄️ Consultando tabela telefones_operadora...');
      const cacheValido60d = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
      
      const { data: cacheOperadora } = await supabase
        .from('telefones_operadora')
        .select('operadora, ultima_consulta')
        .eq('telefone', normalizedPhone)
        .gte('ultima_consulta', cacheValido60d)
        .maybeSingle();

      if (cacheOperadora?.operadora && 
          cacheOperadora.operadora !== 'DESCONHECIDO' && 
          cacheOperadora.operadora !== 'ERRO') {
        const op = normalizarOperadora(cacheOperadora.operadora);
        console.log('[usePhoneOperator] ✅ Operadora da tabela telefones_operadora:', cacheOperadora.operadora, '→', op);
        setOperadora(op);
        if (op) phoneOperatorCache.set(normalizedPhone, op);
        setIsLoading(false);
        return;
      }

      // (Douglas 27/07) NÃO EXISTE BLOQUEIO DE CONSULTA DE OPERADORA. Aqui havia uma
      // trava que, depois de UM erro, devolvia operadora vazia por 30 minutos SEM
      // CONSULTAR — por número, sem aviso e sem como destravar. Nunca foi regra de
      // negócio: era remendo pra conter o laço infinito do efeito (corrigido na raiz
      // em 11c7c4125). Com o laço morto, a trava só servia pra travar venda.
      // Consulta pedida = consulta feita.

      // 4. ÚLTIMA OPÇÃO: Consultar API externa
      console.log('[usePhoneOperator] 🌐 Consultando API externa (gasta crédito!)...');
      const result = await consultarRapida.mutateAsync([normalizedPhone]);

      if (result && result.length > 0 && result[0].OPERADORA) {
        const opBruta = result[0].OPERADORA;

        // (27/07) O MOTIVO VINHA E ERA JOGADO FORA. Quando a consulta falha, o
        // backend manda OPERADORA='ERRO' e o porquê em 'DATA PORTABILIDADE'
        // ('⚠️ Sem saldo', '⚠️ Credenciais', 'HTTP 500'...). O front lia só a
        // operadora e descartava o resto — por isso a tela dizia "verifique o
        // número" sem nunca dizer o que houve. Agora o motivo sobe pra tela.
        if (opBruta === 'ERRO') {
          const motivo = String(result[0]['DATA PORTABILIDADE'] || '').trim();
          console.error('[usePhoneOperator] ❌ API de consulta falhou:', motivo || 'sem detalhe');
          setError(motivo ? `Consulta de operadora falhou — ${motivo}` : 'Consulta de operadora falhou');
          setOperadora(null);
          setIsLoading(false);
          return;
        }

        // ✅ CORREÇÃO: Para FIXO, se API retornar DESCONHECIDO/ERRO, manter como "FIXO"
        if (opBruta === 'DESCONHECIDO' || opBruta === 'ERRO') {
          console.log('[usePhoneOperator] ⚠️ API retornou valor inválido:', opBruta);
          
          // Verificar se é telefone FIXO - se for, retornar "FIXO" como operadora válida
          const tipo = detectType(normalizedPhone);
          if (tipo === 'FIXO') {
            console.log('[usePhoneOperator] 📞 FIXO sem operadora na API, mantendo como FIXO');
            setOperadora('FIXO');
            phoneOperatorCache.set(normalizedPhone, 'FIXO');
            setIsLoading(false);
            return;
          }
          
          // Setar DESCONHECIDO para que o campo exiba claramente que foi consultado
          setOperadora('DESCONHECIDO');
          setIsLoading(false);
          return;
        }
        
        // (27/07) OPERADORA DESCONHECIDA VALE MAIS QUE 'OUTRA'. O normalizador só
        // conhece 5 nomes; qualquer doadora fora deles virava 'OUTRA' — e 'OUTRA'
        // não era guardada no cache, então a consulta se repetia sem fim. Agora
        // preserva o nome que a API mandou (é o dado real da doadora, e portabilidade
        // precisa dele) e SEMPRE guarda, pra consulta encerrar na primeira resposta.
        const opNormalizada = normalizarOperadora(opBruta);
        const op = opNormalizada === 'OUTRA'
          ? String(opBruta).toUpperCase().trim()
          : opNormalizada;
        console.log('[usePhoneOperator] ✅ Operadora da API:', opBruta, '→', op);
        setOperadora(op);

        if (op) {
          phoneOperatorCache.set(normalizedPhone, op);
        }
      } else {
        // ✅ CORREÇÃO: Para FIXO, se API não retornar nada, manter como "FIXO"
        const tipo = detectType(normalizedPhone);
        if (tipo === 'FIXO') {
          console.log('[usePhoneOperator] 📞 FIXO sem resposta da API, mantendo como FIXO');
          setOperadora('FIXO');
          phoneOperatorCache.set(normalizedPhone, 'FIXO');
          setIsLoading(false);
          return;
        }
        throw new Error('Operadora não encontrada');
      }
    } catch (err: any) {
      console.error('[usePhoneOperator] ❌ Erro ao consultar operadora:', err);
      
      if (retryCount < 1) {
        console.log('[usePhoneOperator] 🔄 Tentando novamente em 1s...');
        setTimeout(() => fetchOperadora(normalizedPhone, retryCount + 1), 1000);
        return;
      }

      // Nada de carimbar bloqueio: erro de consulta não pode virar número travado.
      setError(err.message || 'Erro ao consultar');
      setOperadora(null);
    } finally {
      setIsLoading(false);
    }
  }, [consultarRapida]);

  // (27/07) O EFEITO NÃO PODE DEPENDER DE fetchOperadora. `consultarRapida` vem do
  // useMutation e TROCA DE IDENTIDADE a cada mudança de estado da consulta
  // (parada → executando → concluída). Isso recriava fetchOperadora, que mudava a
  // dependência do efeito, que reexecutava e agendava OUTRA consulta — o ciclo se
  // alimentava sozinho e o campo girava pra sempre. A ref mantém sempre a versão
  // mais nova da função sem entrar na lista de dependências.
  const fetchOperadoraRef = useRef(fetchOperadora);
  useEffect(() => {
    fetchOperadoraRef.current = fetchOperadora;
  }, [fetchOperadora]);

  const refetch = useCallback(() => {
    const normalized = validateAndNormalize(phoneNumber);
    if (normalized) {
      phoneOperatorCache.clear(normalized);
      localStorage.removeItem(`phone-operator-consulted:${normalized}`);
      fetchOperadora(normalized);
    }
  }, [phoneNumber, validateAndNormalize, fetchOperadora]);

  useEffect(() => {
    console.log('[usePhoneOperator] useEffect disparado, phoneNumber:', phoneNumber);
    
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // ✅ CORREÇÃO: Para FIXO, também consultar a API para obter a operadora real
    // A API suporta telefones fixos (10 dígitos com DDD) e retorna operadora real (VIVO, NET, OI, etc)
    if (phoneNumber) {
      const tipo = detectType(phoneNumber);
      if (tipo === 'FIXO') {
        console.log('[usePhoneOperator] 📞 Telefone FIXO detectado - consultando API para operadora real');
        // NÃO retornar aqui - deixar continuar para fetchOperadora()
      }
    }

    const normalized = validateAndNormalize(phoneNumber);
    
    if (!normalized) {
      console.log('[usePhoneOperator] ⏭️ Número não validado, pulando consulta');
      setOperadora(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    console.log('[usePhoneOperator] ⏰ Agendando consulta (debounce 1000ms)');
    // Debounce de 1000ms (aumentado de 500ms para reduzir consultas simultâneas)
    debounceTimerRef.current = setTimeout(() => {
      fetchOperadoraRef.current(normalized);
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [phoneNumber, validateAndNormalize]);

  return {
    operadora,
    isLoading,
    error,
    refetch,
  };
}
